/**
 * Permission catalog. Roles are rows in the database so they can be retuned
 * without a deploy, but the permission strings themselves live here so the API
 * guards and the admin UI can never drift apart.
 */
export const PERMISSIONS = [
    // Catalog
    'catalog:read',
    'catalog:write',
    // Inventory
    'inventory:read',
    'inventory:write',
    'inventory:adjust', // stock adjustments — separate because it is audit-sensitive
    'inventory:provision', // advance units through the kosher provisioning chain
    'inventory:request', // ask another location for stock, or cancel your own ask
    // Customers
    'customers:read',
    'customers:write',
    'customers:credit', // adjust a customer's store credit — audit-sensitive, moves value
    // Gift cards (t1102) — issue, void and look up stored-value cards.
    'gift_cards:read',
    'gift_cards:manage',
    // Orders
    'orders:read',
    'orders:write',
    'orders:refund',
    // Repairs
    'repairs:read',
    'repairs:write',
    // Rentals (t0917) — a phone or SIM handed to a customer under IMEI/ICCID
    // tracking, expected back rather than sold.
    'rentals:read',
    'rentals:write',
    // Phone plans (t1083) — the active subscriptions customers pay for.
    'subscriptions:read',
    'subscriptions:write',
    // Kiosks
    'kiosks:read',
    'kiosks:manage',
    // Staff administration
    'staff:read',
    'staff:manage',
    'roles:manage',
    // Reporting / audit
    'reports:read',
    'audit:read',
    // Shop settings — how the website behaves, not how the shop is run
    'settings:manage',
    // Third-party credentials (Stripe, SMS, email) — admin only, deliberately
    // narrower than settings:manage since these can move real money and texts.
    'integrations:manage',
    // Mail — the in-portal inbox. read/send apply to whatever mailboxes a
    // staff member can already see (shared, or assigned to them); manage adds
    // creating mailboxes and assigning them to other staff.
    'mail:read',
    'mail:send',
    'mail:manage',
    // Support (t0799) — the inbound call/SMS ticket queue. read/write cover
    // the queue itself (viewing, replying, transitioning, assigning); routing
    // config (business hours, call targets, voicemail greeting) rides
    // settings:manage instead, alongside the other admin-only shop settings.
    'support:read',
    'support:write',
    // Tasks (t1079) — standalone to-dos and support-ticket follow-ups, assigned
    // to one staff member or to everyone. read lists them, write creates,
    // edits, completes and reopens. review (t1216) approves or sends back a task
    // staff marked done; holders' own completions skip review. Admin only by
    // default — grant it to other roles from the role permissions dialog.
    'tasks:read',
    'tasks:write',
    'tasks:review',
    // AI-assisted content editing (writing help, image generation) — spends
    // metered third-party API credit. Generates content only; saving it still
    // requires the target field's own write permission (catalog:write, etc).
    'ai:assist',
    // Wiki / knowledge base (t0896). read covers browsing the internal wiki —
    // an invited guest holds only this, further narrowed to the sections
    // they've been granted (see modules/wiki/service.ts). write is authoring
    // pages; manage is sections, publishing (public or internal) and guest
    // access, kept separate from write since it decides what the public site
    // can see.
    'wiki:read',
    'wiki:write',
    'wiki:manage',
    // Internal staff workspace (t0895) — Slack-style channels and bulletin
    // boards. read/write apply to whatever spaces a staff member or guest can
    // already see; a guest is narrowed to the spaces granted to them in
    // workspace_space_grants, the same shape wiki_section_grants uses for
    // wiki:read (see modules/workspace/service.ts, mirroring modules/wiki).
    // manage adds creating/archiving spaces and inviting/assigning guests.
    'workspace:read',
    'workspace:write',
    'workspace:manage',
    // Staff time clock (t1106). punch is clocking yourself in and out; manage is
    // viewing everyone's hours and correcting, voiding or adding entries.
    'timeclock:punch',
    'timeclock:manage',
    // Payments & expenses (t1164). read is viewing what was paid to whom; manage
    // is recording, paying and voiding — staff and freelancer pay is sensitive,
    // so only admin and manager hold either by default.
    'expenses:read',
    'expenses:manage',
];
export const ROLE_KEYS = ['admin', 'manager', 'sales', 'technician', 'support', 'guest'];
export const DEFAULT_ROLE_PERMISSIONS = {
    admin: PERMISSIONS,
    manager: [
        'catalog:read',
        'catalog:write',
        'inventory:read',
        'inventory:write',
        'inventory:adjust',
        'inventory:provision',
        'inventory:request',
        'customers:read',
        'customers:write',
        'customers:credit',
        'gift_cards:read',
        'gift_cards:manage',
        'orders:read',
        'orders:write',
        'orders:refund',
        'repairs:read',
        'repairs:write',
        'rentals:read',
        'rentals:write',
        'subscriptions:read',
        'subscriptions:write',
        'kiosks:read',
        'kiosks:manage',
        'staff:read',
        'reports:read',
        'audit:read',
        'settings:manage',
        'mail:read',
        'mail:send',
        'mail:manage',
        'support:read',
        'support:write',
        'tasks:read',
        'tasks:write',
        'ai:assist',
        'wiki:read',
        'wiki:write',
        'wiki:manage',
        'workspace:read',
        'workspace:write',
        'workspace:manage',
        'timeclock:punch',
        'timeclock:manage',
        'expenses:read',
        'expenses:manage',
    ],
    sales: [
        'catalog:read',
        'inventory:read',
        'inventory:request',
        'customers:read',
        'customers:write',
        'gift_cards:read',
        'orders:read',
        'orders:write',
        'repairs:read',
        'repairs:write',
        'rentals:read',
        'rentals:write',
        'subscriptions:read',
        'subscriptions:write',
        'mail:read',
        'mail:send',
        'support:read',
        'support:write',
        'tasks:read',
        'tasks:write',
        'wiki:read',
        'workspace:read',
        'workspace:write',
        'timeclock:punch',
    ],
    technician: [
        'catalog:read',
        'inventory:read',
        'inventory:write',
        'inventory:provision',
        'inventory:request',
        'repairs:read',
        'repairs:write',
        'rentals:read',
        'tasks:read',
        'tasks:write',
        'customers:read',
        'wiki:read',
        'workspace:read',
        'workspace:write',
        'timeclock:punch',
    ],
    support: [
        'catalog:read',
        'inventory:read',
        'customers:read',
        'orders:read',
        'repairs:read',
        'rentals:read',
        'subscriptions:read',
        'mail:read',
        'mail:send',
        'support:read',
        'support:write',
        'tasks:read',
        'tasks:write',
        'wiki:read',
        'workspace:read',
        'workspace:write',
        'timeclock:punch',
    ],
    // An invited outside collaborator (t0896, extended by t0895) — the wiki
    // and/or the workspace, and even that is narrowed to the specific sections
    // or spaces they're granted rather than the whole internal wiki or
    // workspace. See modules/wiki/service.ts and modules/workspace/service.ts.
    guest: ['wiki:read', 'workspace:read', 'workspace:write'],
};
//# sourceMappingURL=permissions.js.map